import { notetakerHears, usd, type AckResult, type CaptionsState } from '@live-stream/shared'
import type { Media } from '../media/media.js'
import type { Room } from '../room.js'
import type { AppSocket } from '../socket.js'
import type { KeyValueStore } from '../storage.js'
import { derived, patch, readonly, watch, writable, type ReadableStore } from '../store.js'
import { Chunker, Downsampler, SpeechGate, CHUNK_SAMPLES, TARGET_RATE } from './captions-audio.js'

/**
 * The note taker, this browser's side (it was "live captions").
 *
 * While a host has the note taker in the meeting, send what this person's
 * own microphone hears — resampled to 16 kHz, a tenth of a second at a
 * time, and only around speech, with half a second of pre-roll so the
 * first word is whole — to be written down under their name. Each person's
 * voice arrives on its own, so every line is theirs without guessing.
 *
 * The microphone is read from a clone of the published track, enabled, so
 * a mute on the call does not starve it — the speaking signal already says
 * false while muted, and the server drops audio from anyone the room shows
 * as muted, so nothing said on mute is ever sent.
 *
 * Once a host adds it, each person is asked — yes, or "don't include me" —
 * and can change their mind while it stays. It hears everyone who has not
 * said no, or, when the host has it ask first, only those who said yes;
 * the server holds that line too. Muted, nobody is heard. Nothing is drawn
 * as captions: what it writes goes to the transcript, the assistant and
 * the notes.
 */

export interface CaptionsControllerState {
  busy: boolean
  error: string | null
  /** "X added the note taker…", for a few seconds after it happens. */
  notice: string | null
  /** Hosts: the room's AI budget for the month is nearly spent. Stays until waved off. */
  budgetNotice: string | null
  sending: boolean
}

/** The processor that hands the microphone's samples over, in 2048-sample runs. */
const TAP = `
class CaptionTap extends AudioWorkletProcessor {
  constructor() { super(); this.buf = new Float32Array(2048); this.n = 0 }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0]
    if (ch) for (let i = 0; i < ch.length; i++) {
      this.buf[this.n++] = ch[i]
      if (this.n === this.buf.length) { this.port.postMessage(this.buf.slice(0)); this.n = 0 }
    }
    return true
  }
}
registerProcessor('caption-tap', CaptionTap)
`

interface Capture {
  stop(): void
}

async function startCapture(
  track: MediaStreamTrack,
  onChunk: (chunk: Int16Array, at: number) => void,
): Promise<Capture> {
  const clone = track.clone()
  clone.enabled = true
  const ctx = new AudioContext()
  const url = URL.createObjectURL(new Blob([TAP], { type: 'application/javascript' }))
  try {
    await ctx.audioWorklet.addModule(url)
  } finally {
    URL.revokeObjectURL(url)
  }
  const source = ctx.createMediaStreamSource(new MediaStream([clone]))
  const tap = new AudioWorkletNode(ctx, 'caption-tap')
  // A node nothing pulls on is not run: a silent path to the speakers keeps it going.
  const sink = ctx.createGain()
  sink.gain.value = 0
  source.connect(tap).connect(sink).connect(ctx.destination)
  const down = new Downsampler(ctx.sampleRate)
  const chunker = new Chunker()
  const chunkMs = (CHUNK_SAMPLES / TARGET_RATE) * 1000
  tap.port.onmessage = (e: MessageEvent<Float32Array>) => {
    for (const chunk of chunker.push(down.push(e.data))) onChunk(chunk, Date.now() - chunkMs)
  }
  if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined)
  return {
    stop() {
      tap.port.onmessage = null
      source.disconnect()
      tap.disconnect()
      clone.stop()
      void ctx.close().catch(() => undefined)
    },
  }
}

/** An ack that never comes is a timeout, not a hang. */
function ask(emit: (done: (r: AckResult) => void) => void): Promise<AckResult> {
  return new Promise<AckResult>((resolve) => {
    const timer = setTimeout(
      () =>
        resolve({ ok: false, error: { code: 'timeout', message: 'No answer from the server.' } }),
      8_000,
    )
    emit((r) => {
      clearTimeout(timer)
      resolve(r)
    })
  })
}

export class Captions {
  private readonly store: ReturnType<typeof writable<CaptionsControllerState>>
  readonly state: ReadableStore<CaptionsControllerState>
  /** The room's captions: on or off, since when, by whom. */
  readonly room: ReadableStore<CaptionsState>
  readonly active: ReadableStore<boolean>
  /** The server can caption this meeting (a transcription service is configured). */
  readonly available: ReadableStore<boolean>
  /** The room's AI switch: off, captions may not be turned on (they may still be turned off). */
  readonly aiEnabled: ReadableStore<boolean>
  readonly canToggleRoom: ReadableStore<boolean>
  readonly canTurnOn: ReadableStore<boolean>
  /** This person's answer to the note taker in the meeting now; null until they give one. */
  readonly myAnswer: ReadableStore<'in' | 'out' | null>
  /** The note taker hears this person (when they are not muted). */
  readonly hearsMe: ReadableStore<boolean>
  /** It is in the meeting and this person has not answered yet: ask them. */
  readonly askNeeded: ReadableStore<boolean>
  /** How many of the people here it hears, for its row in Participants. */
  readonly hearing: ReadableStore<{ heard: number; everyone: number }>
  private noticeTimer: ReturnType<typeof setTimeout> | null = null
  private capture: Capture | null = null
  private starting = false
  private readonly gate = new SpeechGate()
  private boundTo: AppSocket | null = null

  constructor(
    private readonly meeting: Room,
    private readonly media: Media,
    _local: KeyValueStore | null,
  ) {
    this.store = writable<CaptionsControllerState>({
      busy: false,
      error: null,
      notice: null,
      budgetNotice: null,
      sending: false,
    })
    this.state = readonly(this.store)

    const r = meeting.state
    this.room = derived([r], (s) => s.room.captions)
    this.active = derived([this.room], (c) => c.active)
    this.available = derived([r], (s) => s.captionsAvailable)
    this.aiEnabled = derived([r], (s) => s.room.aiEnabled)
    const moderator = derived([r], (s) => s.myRole === 'host' || s.myRole === 'cohost')
    this.canToggleRoom = derived([moderator, this.available], (m, a) => m && a)
    this.canTurnOn = derived([this.canToggleRoom, this.aiEnabled], (t, ai) => t && ai)
    const mine = derived([r], (s) => (s.me ? s.room.participants[s.me] : undefined))
    this.myAnswer = derived([mine], (p) => p?.notetaker ?? null)
    this.hearsMe = derived([this.room, mine], (c, p) => notetakerHears(c, p))
    this.askNeeded = derived(
      [this.active, r, this.myAnswer],
      (on, s, a) => on && s.phase === 'joined' && a === null,
    )
    this.hearing = derived([r], (s) => {
      const people = Object.values(s.room.participants)
      return {
        heard: people.filter((p) => notetakerHears(s.room.captions, p)).length,
        everyone: people.length,
      }
    })
    watch(
      meeting.socket,
      (s) => s,
      (sock) => this.bind(sock),
      { immediate: true },
    )

    watch(
      this.active,
      (on) => on,
      (on, was) => this.onActive(on, was),
    )

    // What goes out: start on the microphone while cleared to, and start
    // over on a new microphone.
    const track = derived([media.local.state], (l) => l.audioTrack)
    const shouldSend = derived(
      [this.hearsMe, r, track],
      (hears, s, t) => hears && s.phase === 'joined' && t !== null,
    )
    let lastTrack: MediaStreamTrack | null = track.get()
    const decide = (): void => {
      const t = track.get()
      if (this.capture && t !== lastTrack) this.end()
      lastTrack = t
      if (shouldSend.get()) void this.begin()
      else this.end()
    }
    watch(shouldSend, (x) => x, decide, { immediate: true })
    watch(track, (t) => t, decide)

    // Speech after a long quiet: the stream may have been let go (ten
    // minutes idle). Warm it again.
    watch(
      media.speaking,
      (x) => x,
      (speaking, was) => {
        if (speaking && !was && this.store.get().sending)
          meeting.socket.get()?.emit('captions:warm')
      },
    )
  }

  private readonly onBudget = (msg: {
    spentUsd: number
    capUsd: number
    fraction: number
  }): void => {
    patch(this.store, {
      budgetNotice: `This room has used ${Math.round(msg.fraction * 100)}% of its AI budget for the month (${usd(msg.spentUsd)} of ${usd(msg.capUsd)}). At the limit, the note taker leaves, and notes and assistant answers are refused until next month or a higher budget.`,
    })
  }

  private bind(sock: AppSocket | null): void {
    if (this.boundTo === sock) return
    this.boundTo?.off('ai:budget', this.onBudget)
    this.boundTo = sock
    sock?.on('ai:budget', this.onBudget)
  }

  private say(text: string | null): void {
    patch(this.store, { notice: text })
    if (this.noticeTimer) clearTimeout(this.noticeTimer)
    if (text) this.noticeTimer = setTimeout(() => patch(this.store, { notice: null }), 9_000)
  }

  private onActive(on: boolean, was: boolean | undefined): void {
    const cc = this.room.get()
    // Everyone else is asked (askNeeded); whoever added it is told it is in.
    if (on && was === false && cc.startedBy !== this.meeting.state.get().me) {
      this.say(null)
    } else if (on && was === false) {
      this.say(
        "You added the note taker. It writes down what everyone says, for the meeting's notes and the assistant. Everyone is asked if they want to be included.",
      )
    } else if (!on && was === true) {
      this.say(cc.endedReason ?? 'The note taker has left.')
    }
  }

  private async begin(): Promise<void> {
    const track = this.media.local.state.get().audioTrack
    if (this.capture || this.starting || !track) return
    this.starting = true
    try {
      this.capture = await startCapture(track, (chunk, at) => {
        for (const out of this.gate.push(chunk, at, this.media.speaking.get())) {
          const sock = this.meeting.socket.get()
          if (!sock?.connected) continue
          // A copy: the gate may hand the same buffer on again.
          const pcm = out.chunk.slice().buffer
          sock.emit('captions:audio', { pcm, at: out.at })
        }
      })
      patch(this.store, { sending: true })
      // Open the transcription stream now, so the first word does not wait for it.
      this.meeting.socket.get()?.emit('captions:warm')
    } catch (err) {
      patch(this.store, {
        error: `The note taker could not hear your microphone: ${err instanceof Error ? err.message : err}`,
      })
    } finally {
      this.starting = false
    }
  }

  private end(): void {
    this.capture?.stop()
    this.capture = null
    patch(this.store, { sending: false })
  }

  private async call(event: 'captions:start' | 'captions:stop'): Promise<void> {
    const sock = this.meeting.socket.get()
    if (!sock) return
    patch(this.store, { busy: true, error: null })
    try {
      const res = await ask((done) => sock.emit(event, done))
      if (!res.ok) patch(this.store, { error: res.error.message })
    } finally {
      patch(this.store, { busy: false })
    }
  }

  /** Hosts and cohosts: add the note taker to the meeting. */
  startForRoom(): Promise<void> {
    return this.call('captions:start')
  }

  /** Hosts and cohosts: the note taker leaves. */
  stopForRoom(): Promise<void> {
    return this.call('captions:stop')
  }

  /**
   * This person's answer: include me, or don't. Given while it stays, it
   * can be changed any time; it is logged with the time.
   */
  async answer(choice: 'in' | 'out'): Promise<void> {
    const sock = this.meeting.socket.get()
    const startedAt = this.room.get().startedAt
    if (!sock || startedAt === undefined) return
    patch(this.store, { busy: true, error: null })
    try {
      const res = await ask((done) => sock.emit('captions:consent', { startedAt, choice }, done))
      if (!res.ok) patch(this.store, { error: res.error.message })
    } finally {
      patch(this.store, { busy: false })
    }
  }

  dismissBudgetNotice(): void {
    patch(this.store, { budgetNotice: null })
  }
}
